import { BaseService } from "./baseService";
import { Task, ListResponse } from "../types";
import { buildSelectQuery } from "../utils/apiHelpers";
import { generateCacheKey, networkFirst } from "../storage/cacheManager";
import {
  requestDeduplication,
  generateRequestKey,
} from "../utils/requestDeduplication";
import apiClient from "./api";

class SubtaskService extends BaseService<Task> {
  constructor() {
    super("Task");
    this.setCacheStrategy("network-first");
    this.setCacheExpiry(5 * 60 * 1000);
  }

  async getSubtasks(taskId: string): Promise<ListResponse<Task>> {
    const selectFields = [
      "id",
      "name",
      "status",
      "assignedUserId",
      "assignedUserName",
      "dateStart",
      "dateEnd",
      "dateEndDate",
      "description",
      "cSolution",
      "cNumber",
      "createdAt",
    ];
    const selectQuery = buildSelectQuery(selectFields);
    const url = `/Task/${taskId}/tasks?select=${selectQuery}&maxSize=15&offset=0&order=desc`;
    const cacheKey = generateCacheKey(`/Task/${taskId}/tasks`, {
      select: selectFields,
      maxSize: 15,
      offset: 0,
      order: "desc",
    });
    const requestKey = generateRequestKey("GET", url);

    const fetchData = async (
      signal?: AbortSignal
    ): Promise<ListResponse<Task>> => {
      const response = await apiClient.get<ListResponse<Task>>(url, { signal });
      return response.data;
    };

    const deduplicatedFetch = () =>
      requestDeduplication.execute(requestKey, fetchData);

    return networkFirst(cacheKey, deduplicatedFetch, this.cacheExpiry);
  }

  async createSubtask(
    parentId: string,
    taskData: Partial<Task>
  ): Promise<Task> {
    return this.create({ ...taskData, taskParentId: parentId });
  }

  async updateSubtask(subtaskId: string, data: Partial<Task>): Promise<Task> {
    return this.update(subtaskId, data);
  }

  async deleteSubtask(subtaskId: string): Promise<void> {
    return this.delete(subtaskId);
  }
}

export const subtaskService = new SubtaskService();

export type { Task };
export const getSubtasks = (taskId: string) =>
  subtaskService.getSubtasks(taskId);
export const createSubtask = (parentId: string, taskData: Partial<Task>) =>
  subtaskService.createSubtask(parentId, taskData);
export const updateSubtask = (subtaskId: string, data: Partial<Task>) =>
  subtaskService.updateSubtask(subtaskId, data);
export const deleteSubtask = (subtaskId: string) =>
  subtaskService.deleteSubtask(subtaskId);
